import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useDebounce from "../../hooks/useDebounce";
import AppBar from "../../common/AppBar";
import AppTable from "../../common/AppTable/AppTable";
import Loader from "../../common/Loader";
import Snackbar from "../../common/Snackbar";
import {
  fetchBackups,
  createBackup,
  restoreBackup,
  deleteBackup,
  exportBackupCSV,
} from "../../redux/backupSlice";
import { getAllAppSetups } from "../../redux/appSetupSlice";
import { Button } from "../../common/Buttons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotateRight,
  faTrashCan,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { DeleteRowButton } from "../../common/Buttons/TableButtons";
import DeleteConfirmation from "../../common/Confirmations/DeleteConfirmation";
import ExportCsvBtn from "../../common/AppSpecificConpnents/ExportCsvBtn";
import {
  TextField,
  TextArea,
  CheckboxField,
  SignatureField,
} from "../../common/FormFields";
import "./Backups.css";
import {
  formatDateToAppTimezone,
  getAppTimezoneName,
  getAppTimezoneAbbr,
} from "../../util/timezoneUtils";
import { BASE_URL } from "../../config";
import { getAuditTrailData } from "../../util/getAuditTrailData";
import { generateSHA256 } from "../../util/generateSHA256";
import { lockTask, unlockTask, pingTask } from "../../util/apiService";
import useApiTabLock from "../../hooks/useApiTabLock";
import useTabOrderLock from "../../hooks/useTabOrderLock";

const columns = [
  {
    headName: "Date",
    field: "backup_date",
    sort: false,
    filter: true,
    width: "200px",
  },
  {
    headName: "Name",
    field: "backup_name",
    sort: false,
    filter: true,
    width: "200px",
  },
  {
    headName: "Type",
    field: "backup_type",
    sort: false,
    filter: true,
    width: "200px",
  },
  { headName: "Action", field: "action", width: "150px", textCenter: false },
];

const Backups = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { backups, loading, totalBackups, lastBackupDate } = useSelector(
    (state) => state.backups,
  );
  const authUser = useSelector((state) => state.auth.user);

  // Use full URL as route key to track tabs per route independently
  const routeKey = `backup_${window.location.origin}${window.location.pathname}`;

  // Tab lock management - only one tab can perform actions
  const {
    isLocked: isApiLocked,
    isActiveTab,
    lockedBy,
  } = useApiTabLock("backup");

  // Tab order lock - first tab (by registration order) gets edit rights
  const { isFirstTab: isFirstTabByOrder, isLocked: isLockedByOrder } =
    useTabOrderLock(routeKey);

  const [visibleColumns, setVisibleColumns] = useState(columns);
  const [searchQuery, setSearchQuery] = useState("");
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [currentApiQuery, setCurrentApiQuery] = useState("");
  const [localLoading, setLocalLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [activeDownloads, setActiveDownloads] = useState(new Map()); // backup_id -> download info
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  // Create backup form state
  const [backupName, setBackupName] = useState("");

  // Restore backup modal state
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [restoreBackupData, setRestoreBackupData] = useState({
    restoreComments: "",
    acknowledgeBackup: false,
    password: "",
    signature: "",
    restorer: localStorage.getItem("user_name") || "",
  });
  const restoreSigPadRef = React.useRef();
  const fileInputRef = React.useRef(null);
  const backupsRef = React.useRef([]);

  // Update ref when backups change
  useEffect(() => {
    backupsRef.current = backups || [];
  }, [backups]);

  // Get signature auth methods from localStorage
  const [authMethods, setAuthMethods] = useState([]);
  useEffect(() => {
    try {
      const storedMethods = localStorage.getItem("signature_auth_methods");
      if (storedMethods) {
        const methods = JSON.parse(storedMethods);
        setAuthMethods(Array.isArray(methods) ? methods : []);
      } else {
        const userInfo = localStorage.getItem("loggedinUserInfo");
        if (userInfo) {
          try {
            const parsed = JSON.parse(userInfo);
            if (
              parsed.signature_auth_methods &&
              Array.isArray(parsed.signature_auth_methods)
            ) {
              setAuthMethods(parsed.signature_auth_methods);
            } else {
              setAuthMethods(["password", "signature"]);
            }
          } catch (e) {
            setAuthMethods(["password", "signature"]);
          }
        } else {
          setAuthMethods(["password", "signature"]);
        }
      }
    } catch (error) {
      console.error("Error parsing signature_auth_methods:", error);
      setAuthMethods(["password", "signature"]);
    }
  }, []);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const formatDateOnly = (yyyy, mm, dd) => `${yyyy}-${mm}-${dd}`;

  const pad2 = (n) => String(n).padStart(2, "0");

  // -----------------------------
  // FORMATTERS (NO TZ CONVERSION)
  // -----------------------------
  const formatToApiDateTime = (date) =>
    `${date.getFullYear()}-${pad2(
      date.getMonth() + 1,
    )}-${pad2(date.getDate())} ${pad2(
      date.getHours(),
    )}:${pad2(date.getMinutes())}`;

  const convertTimeOnly = (timeStr) => {
    const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);

    if (!match) return null;

    let [, hour, minute, period] = match;

    hour = Number(hour);
    minute = Number(minute);

    // If no AM/PM period is provided, assume AM
    if (!period) {
      period = "AM";
    }

    if (period.toUpperCase() === "PM" && hour !== 12) hour += 12;
    if (period.toUpperCase() === "AM" && hour === 12) hour = 0;

    return `${pad2(hour)}:${pad2(minute)}`;
  };

  const stripTimezoneWords = (value) =>
    value.replace(/\b(UTC|EST|EDT|PST|PDT|CST|CDT|MST|MDT|GMT)\b/gi, "").trim();

  // Convert search time to UTC based on app timezone
  const convertSearchTimeToUtc = (timeStr) => {
    try {
      const appTimezone = getAppTimezoneName(appSetupState);

      // If app timezone is UTC, return as-is
      if (appTimezone === "UTC") {
        return timeStr;
      }

      // Parse the time string - handle both 12-hour and 24-hour formats
      const [time, period] = timeStr.trim().split(/\s+/);
      const [hour, minute] = time.split(":");

      let hours = parseInt(hour, 10);
      const minutes = parseInt(minute, 10);

      // Handle AM/PM (only if period is present)
      if (period) {
        if (period.toUpperCase() === "PM" && hours !== 12) {
          hours += 12;
        }
        if (period.toUpperCase() === "AM" && hours === 12) {
          hours = 0;
        }
      }
      // If no period, assume it's already in 24-hour format (from convertTimeOnly)

      // Simple timezone offset mapping
      let offsetHours = 0;
      switch (appTimezone) {
        case "America/Los_Angeles": // PST/PDT
          offsetHours = 8; // PST is UTC-8 (standard time)
          break;
        case "America/New_York": // EST/EDT
          offsetHours = 5; // EST is UTC-5 (standard time)
          break;
        case "America/Chicago": // CST/CDT
          offsetHours = 6; // CST is UTC-6 (standard time)
          break;
        case "America/Denver": // MST/MDT
          offsetHours = 7; // MST is UTC-7 (standard time)
          break;
        default:
          // For unknown timezones, try to detect automatically
          const now = new Date();
          const utcTime = new Date(
            now.getTime() + now.getTimezoneOffset() * 60000,
          );
          const localTime = new Date(
            now.toLocaleString("en-US", { timeZone: appTimezone }),
          );
          offsetHours =
            (utcTime.getTime() - localTime.getTime()) / (1000 * 60 * 60);
          break;
      }

      // Convert to UTC by adding the offset
      let utcHours = hours + offsetHours;

      // Handle overflow/underflow
      if (utcHours >= 24) {
        utcHours -= 24;
      } else if (utcHours < 0) {
        utcHours += 24;
      }

      const result = `${utcHours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
      return result;
    } catch (err) {
      console.warn("Time conversion failed:", err);
      return timeStr; // fallback to original
    }
  };

  // Convert datetime search string to UTC based on app timezone
  const convertSearchDateTimeToUtc = (dateTimeStr) => {
    try {
      const appTimezone = getAppTimezoneName(appSetupState);

      // If app timezone is UTC, return as-is
      if (appTimezone === "UTC") {
        return formatToApiDateTime(new Date(dateTimeStr));
      }

      // Parse the datetime string as if it's in app timezone
      const date = new Date(dateTimeStr);
      if (isNaN(date.getTime())) {
        return formatToApiDateTime(date); // fallback
      }

      // Get the UTC representation and app timezone representation
      const utcDate = new Date(
        date.toLocaleString("en-US", { timeZone: "UTC" }),
      );
      const appTzDate = new Date(
        date.toLocaleString("en-US", { timeZone: appTimezone }),
      );

      // Calculate the offset and apply it to get correct UTC time
      const offsetMs = utcDate.getTime() - appTzDate.getTime();
      const correctedUtcTime = new Date(date.getTime() + offsetMs);

      const result = formatToApiDateTime(correctedUtcTime);
      return result;
    } catch (err) {
      console.warn("DateTime conversion failed:", err);
      return formatToApiDateTime(new Date(dateTimeStr)); // fallback
    }
  };

  // -----------------------------
  // MAIN PARSER
  // -----------------------------
  const getSearchParams = useCallback(() => {
    let search = "";
    let searchType = "";
    let sortBy = "";
    let sortOrder = "";

    if (currentApiQuery) {
      const params = new URLSearchParams(currentApiQuery);

      search = params.get("search") || "";
      searchType = params.get("search_type") || "";
      sortBy = params.get("sort_by") || "";
      sortOrder = params.get("sort_order") || "";

      if (search && searchType) {
        const searchTypes = searchType.split(",").map((s) => s.trim());

        const isSearchingByBackupDate = searchTypes.includes("backup_date");

        if (isSearchingByBackupDate) {
          let trimmed = search.trim();

          try {
            trimmed = trimmed.replace(",", "");
            trimmed = stripTimezoneWords(trimmed);

            // ------------------
            // TIME ONLY
            // ------------------
            const timeOnly = convertTimeOnly(trimmed);
            if (timeOnly) {
              search = convertSearchTimeToUtc(timeOnly);
              return { search, searchType, sortBy, sortOrder };
            }

            // ------------------
            // MM-DD-YYYY  (STOP HERE)
            // ------------------
            if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
              const [mm, dd, yyyy] = trimmed.split("-");
              search = `${yyyy}-${mm}-${dd}`;
              return { search, searchType, sortBy, sortOrder };
            }

            // ------------------
            // SINGLE NUMBER
            // ------------------
            if (/^\d{1,2}$/.test(trimmed)) {
              search = trimmed;
              return { search, searchType, sortBy, sortOrder };
            }

            // ------------------
            // YEAR ONLY
            // ------------------
            if (/^\d{4}$/.test(trimmed)) {
              search = trimmed;
              return { search, searchType, sortBy, sortOrder };
            }

            // ------------------
            // MM-DD
            // ------------------
            if (/^\d{2}-\d{2}$/.test(trimmed)) {
              search = trimmed;
              return { search, searchType, sortBy, sortOrder };
            }

            // ------------------
            // DD/MM/YYYY
            // ------------------
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
              const [dd, mm, yyyy] = trimmed.split("/");
              search = `${yyyy}-${mm}-${dd}`;
              return { search, searchType, sortBy, sortOrder };
            }

            // ------------------
            // YYYY-MM-DD
            // ------------------
            if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
              search = trimmed;
              return { search, searchType, sortBy, sortOrder };
            }

            // ------------------
            // ISO
            // ------------------
            if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
              const d = new Date(trimmed);
              if (!isNaN(d.getTime())) {
                search = convertSearchDateTimeToUtc(trimmed);
                return { search, searchType, sortBy, sortOrder };
              }
            }

            // ------------------
            // LAST RESORT
            // ------------------
            const parsed = new Date(trimmed);
            if (!isNaN(parsed.getTime())) {
              search = convertSearchDateTimeToUtc(trimmed);
              return { search, searchType, sortBy, sortOrder };
            }
          } catch (err) {
            console.warn("Date parse failed:", err);
          }
        }
      }
    }

    return {
      search,
      searchType,
      sortBy,
      sortOrder,
    };
  }, [currentApiQuery]);

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleFilterSort = useCallback(
    (queryString) => {
      // Remove limit and offset from AppTable's query string
      // We'll manage those ourselves separately
      const params = new URLSearchParams(queryString);
      params.delete("limit");
      params.delete("offset");
      const cleanedQuery = params.toString();

      // Only update if the query actually changed
      if (cleanedQuery !== currentApiQuery) {
        setCurrentApiQuery(cleanedQuery);
      }
    },
    [currentApiQuery],
  );

  useEffect(() => {
    const { search, searchType, sortBy, sortOrder } = getSearchParams();

    dispatch(
      fetchBackups({
        limit,
        offset,
        search,
        searchType,
        sortBy,
        sortOrder,
      }),
    );
  }, [dispatch, limit, offset, currentApiQuery, getSearchParams]);

  // Fetch app setup data on component mount
  useEffect(() => {
    dispatch(getAllAppSetups());
  }, [dispatch]);

  // Lock API call on page load
  useEffect(() => {
    if (authUser && isFirstTabByOrder) {
      // Small delay to ensure tab registration is complete
      const timer = setTimeout(async () => {
        try {
          await lockTask({
            task_id: "backup",
            user_id: authUser.id,
            user_name: `${authUser.first_name} ${authUser.last_name}`,
            action: "edit",
          });
        } catch (error) {
          console.error("Failed to lock backup:", error);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [authUser, isFirstTabByOrder]);

  // Ping API call every minute
  useEffect(() => {
    if (authUser && isFirstTabByOrder) {
      const pingInterval = setInterval(async () => {
        try {
          await pingTask({
            task_id: "backup",
            user_id: authUser.id,
          });
        } catch (error) {
          console.error("Failed to ping backup:", error);
        }
      }, 60000); // 60 seconds = 1 minute

      return () => clearInterval(pingInterval);
    }
  }, [authUser, isFirstTabByOrder]);

  // Handle back button click - unlock API
  const handleAppBarBack = async () => {
    if (authUser && isFirstTabByOrder) {
      try {
        await unlockTask({
          task_id: "backup",
          user_id: authUser.id,
        });
      } catch (error) {
        console.error("Failed to unlock backup:", error);
      }
    }
    navigate(-1);
  };

  const handleCreateBackup = async () => {
    if (!backupName.trim()) {
      showSnackbar("Please enter a backup name.", "error");
      return;
    }

    try {
      setIsActionLoading(true);
      const now = new Date().toISOString();

      // Get user name from localStorage
      const userName = localStorage.getItem("user_name") || "";

      // Prepare audit trail data
      const hash = await generateSHA256(Math.random().toString());
      const auditTrailData = await getAuditTrailData({
        action: "CREATE_BACKUP",
        comment: `Created backup: ${backupName.trim()}`,
        parent_version: null,
        version_content: {
          action_to_show: "Created a Backup",
          backup_name: backupName.trim(),
          backup_type: userName || "Manual",
        },
      });

      // Add hash to audit trail data
      auditTrailData.hash = hash;

      const backupData = {
        backup_date: now,
        backup_name: backupName.trim(),
        backup_location: "uploads/db_backup/",
        backup_type: userName ? "By User " + userName : "Manual",
        audit_trail_data: auditTrailData,
      };

      await dispatch(createBackup(backupData)).unwrap();
      setBackupName("");
      const { search, searchType, sortBy, sortOrder } = getSearchParams();
      dispatch(
        fetchBackups({ limit, offset, search, searchType, sortBy, sortOrder }),
      );
      showSnackbar("Backup created successfully.", "success");
    } catch (error) {
      // Extract error message from various possible locations
      // When using .unwrap(), the error is the value passed to rejectWithValue()
      // which could be a string (message) or an object
      let errorMessage = "Failed to create backup.";

      // Check if error is a string (from rejectWithValue passing message directly)
      if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object") {
        // Check for {success: false, message: "Missing audit trail data"} structure
        if (error.success === false && error.message) {
          errorMessage = error.message;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.error) {
          errorMessage = error.error;
        }
      }

      // Fallback to checking nested structures
      if (errorMessage === "Failed to create backup.") {
        errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          error?.payload?.message ||
          error?.payload?.error ||
          (error?.payload &&
            typeof error.payload === "object" &&
            error.payload.message) ||
          (error?.payload &&
            typeof error.payload === "object" &&
            error.payload.error) ||
          "Failed to create backup.";
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDownload = async (backup) => {
    // Check if backup is in progress
    if (backup.status === "inprogress") {
      showSnackbar(
        "Backup is in progress. Please wait for it to complete before downloading.",
        "info",
      );
      return;
    }

    // Check if this backup is already being downloaded
    const backupId = backup.id || backup.backup_id;
    if (activeDownloads.has(backupId)) {
      showSnackbar(
        "This backup is already being downloaded. Please wait for it to complete.",
        "info",
      );
      return;
    }

    try {
      let downloadUrl;
      let fileName = `${backup.backup_name || "backup"}-${Date.now()}`;
      let isDirectUrl = false;

      // Use backup_location if available (S3 URLs or local paths)
      if (backup.backup_location) {
        // If it's a full URL (S3), use it directly without fetching
        if (
          backup.backup_location.startsWith("http://") ||
          backup.backup_location.startsWith("https://")
        ) {
          downloadUrl = backup.backup_location;
          isDirectUrl = true;
          // Extract file extension from URL if available
          const urlParts = backup.backup_location.split("/");
          const lastPart = urlParts[urlParts.length - 1];
          if (lastPart.includes(".")) {
            const ext = lastPart.split(".").pop();
            fileName = `${backup.backup_name || "backup"}.${ext}`;
          }
        } else {
          // Local path - construct full URL
          downloadUrl = `${BASE_URL}${backup.backup_location}`;
          isDirectUrl = false;
        }
      } else {
        // Fallback to API endpoint
        downloadUrl = `${BASE_URL}backup/download/${backupId}`;
        fileName = `${backup.backup_name || "backup"}.sql`;
        isDirectUrl = false;
      }

      // For direct URLs (S3), just create a link and download directly
      if (isDirectUrl) {
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", fileName);
        link.setAttribute("target", "_blank");
        document.body.appendChild(link);
        link.click();
        link.remove();
        showSnackbar("Backup download initiated.", "success");

        // Start monitoring this download
        monitorDownload(backupId, downloadUrl);
        return;
      }

      // For API endpoints, fetch with authentication
      setIsActionLoading(true);

      // Get auth token from storage
      const token =
        localStorage.rememberMe === "true"
          ? localStorage.token
          : sessionStorage.token;

      const fetchOptions = {
        headers: {},
      };

      if (token) {
        fetchOptions.headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(downloadUrl, fetchOptions);

      if (!response.ok) {
        throw new Error("Failed to download backup");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar("Backup downloaded successfully.", "success");

      // Start monitoring this download
      monitorDownload(backupId, downloadUrl);
    } catch (error) {
      console.error("Download error:", error);
      showSnackbar("Failed to download backup.", "error");
    } finally {
      setIsActionLoading(false);
    }
  };

  // Get app setup state for timezone (defined before formatDate to ensure it's available)
  const appSetupState = useSelector((state) => state.appSetup);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      // Get timezone dynamically each time to ensure we use the latest app setup
      const currentTimezone = getAppTimezoneName(appSetupState);
      const currentTzAbbr = getAppTimezoneAbbr(appSetupState);

      const date = new Date(dateString);

      // Always perform proper timezone conversion
      // Convert UTC datetime to the configured app timezone
      const options = {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: currentTimezone,
      };
      const formattedDate =
        date.toLocaleString("en-US", options) + " " + currentTzAbbr;
      return formattedDate.replace(/\//g, "-");
    } catch (error) {
      return dateString;
    }
  };

  // Helper function to check if a backup is a system backup
  const isSystemBackup = (backupName) => {
    if (!backupName) return false;
    return (
      backupName.startsWith("auto_backup_") ||
      backupName.startsWith("restore_from_auto_backup_")
    );
  };

  // Memoize system backup numbering to avoid recalculating on every render
  const systemBackupNumbers = useMemo(() => {
    if (!backups || backups.length === 0) return new Map();

    // Filter to get only system backups (excluding pre_restore)
    const systemBackups = backups
      .map((b, idx) => ({ backup: b, originalIndex: idx }))
      .filter(
        ({ backup }) =>
          isSystemBackup(backup.backup_name) &&
          !backup.backup_name.startsWith("pre_restore_"),
      );

    // Create a map of backup identifier to sequential number
    const numberMap = new Map();
    systemBackups.forEach(({ backup }, systemIndex) => {
      // Use backup_id as primary key, fallback to backup_name + backup_date
      const key =
        backup.backup_id || `${backup.backup_name}_${backup.backup_date}`;
      // Calculate number: count system backups before current page + index in current page + 1
      // We estimate system backups on previous pages by assuming similar distribution
      // For more accuracy, we'd need total system backup count from API
      const backupNumber = offset + systemIndex + 1;
      numberMap.set(key, backupNumber);
    });

    return numberMap;
  }, [backups, offset]);

  const formatBackupName = (name, index = null, backup = null) => {
    if (!name) return "N/A";

    // Handle "pre_restore_*" pattern - Backup before Restore
    if (name.startsWith("pre_restore_")) {
      return "Backup before Restore";
    }

    // Handle system backups (auto_backup_* and restore_from_auto_backup_*)
    const isSystem = isSystemBackup(name);
    if (isSystem) {
      if (backup) {
        // Use backup object to get the number from our memoized map
        const key =
          backup.backup_id || `${backup.backup_name}_${backup.backup_date}`;
        const backupNumber = systemBackupNumbers.get(key);
        if (backupNumber !== undefined) {
          return `System Backup ${backupNumber}`;
        }
      } else if (
        index !== null &&
        index !== undefined &&
        backups &&
        backups[index]
      ) {
        // Fallback: use index to find backup
        const backupObj = backups[index];
        const key =
          backupObj.backup_id ||
          `${backupObj.backup_name}_${backupObj.backup_date}`;
        const backupNumber = systemBackupNumbers.get(key);
        if (backupNumber !== undefined) {
          return `System Backup ${backupNumber}`;
        }
      }
      return "System Backup";
    }

    return name;
  };

  const formatBackupType = (type, status, backupName) => {
    if (!type) return "N/A";

    // Check if this is an auto backup by name pattern
    const isAutoBackup = backupName && backupName.startsWith("auto_backup_");

    // If backup_type is "Auto" (case-insensitive) or status is "auto", show "By System"
    if (type && type.toLowerCase() === "auto") {
      return "By System";
    }
    if (status && status.toLowerCase() === "auto") {
      return "By System";
    }

    // If it's an auto backup and type contains "System", show only "By System"
    if (isAutoBackup && type && type.includes("System")) {
      return "By System";
    }

    // If type already starts with "By " (e.g., "By System", "By Manual"), return as-is
    if (type.startsWith("By ")) {
      return type;
    }

    // Otherwise, show the backup_type from response
    if (type === "Manual" || type === "System") {
      return `By ${type}`;
    }
    if (type.includes("User")) {
      return "User " + type;
    }
    return `By User ${type}`;
  };

  // Format backup type for restore modal (remove "By " prefix)
  const formatBackupTypeForRestore = (type, status, backupName) => {
    const formattedType = formatBackupType(type, status, backupName);
    // Remove "By " prefix if present
    if (formattedType.startsWith("By ")) {
      return formattedType.replace("By ", "");
    }
    return formattedType;
  };

  // Format date for restore modal: DD-MM-YYYY at HH:MM AM/PM EST
  const formatDateForRestore = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const currentTimezone = getAppTimezoneName(appSetupState);
      const currentTzAbbr = getAppTimezoneAbbr(appSetupState);

      const date = new Date(dateString);

      // Convert to app timezone
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: currentTimezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      const parts = formatter.formatToParts(date);
      const year = parts.find((p) => p.type === "year")?.value || "";
      const month = parts.find((p) => p.type === "month")?.value || "";
      const day = parts.find((p) => p.type === "day")?.value || "";
      const hour = parts.find((p) => p.type === "hour")?.value || "";
      const minute = parts.find((p) => p.type === "minute")?.value || "";
      const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value || "";

      // Format as DD-MM-YYYY at HH:MM AM/PM EST
      const formattedDate = `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year}`;
      const formattedTime = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")} ${dayPeriod.toUpperCase()}`;

      return `${formattedDate} at ${formattedTime} ${currentTzAbbr}`;
    } catch (error) {
      return dateString;
    }
  };

  const isAutoOrSystemBackup = (backup) => {
    if (!backup) return false;

    // Check if backup name starts with "auto_backup_"
    const isAutoBackup =
      backup.backup_name && backup.backup_name.startsWith("auto_backup_");

    // Check if backup_type indicates system/auto backup
    const isSystemType =
      backup.backup_type &&
      (backup.backup_type.toLowerCase() === "auto" ||
        backup.backup_type === "By System" ||
        backup.backup_type.includes("System"));

    // Check if status indicates auto backup
    const isAutoStatus =
      backup.status && backup.status.toLowerCase() === "auto";

    return isAutoBackup || isSystemType || isAutoStatus;
  };

  // Check if backup is in restore status (restoring, restored-complete, restored)
  const isRestoreStatus = (backup) => {
    if (!backup || !backup.status) return false;
    const status = String(backup.status).toLowerCase().trim();
    return (
      status === "restoring" ||
      status === "restored-complete" ||
      status === "restored" ||
      status === "restored_complete"
    );
  };

  // Check if delete button should be disabled
  const isDeleteDisabled = (backup) => {
    return isAutoOrSystemBackup(backup) || isRestoreStatus(backup);
  };

  // Get delete button title based on disable reason
  const getDeleteButtonTitle = (backup) => {
    if (isRestoreStatus(backup)) {
      return "Cannot delete backup that is being restored or has been restored";
    }
    if (isAutoOrSystemBackup(backup)) {
      return "Cannot delete system/auto backups";
    }
    return "Delete";
  };

  const handleRestoreClick = (backup) => {
    setSelectedBackup(backup);
    setRestoreBackupData({
      restoreComments: "",
      acknowledgeBackup: false,
      password: "",
      signature: "",
      restorer: localStorage.getItem("user_name") || "",
    });
    setIsRestoreModalOpen(true);
  };

  const handleRestoreSignatureEnd = () => {
    const canvas = restoreSigPadRef.current?.getCanvas();
    if (canvas) {
      const dataUrl = canvas.toDataURL("image/png");
      setRestoreBackupData((prev) => ({
        ...prev,
        signature: dataUrl,
      }));
    }
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup) return;

    if (!restoreBackupData.acknowledgeBackup) {
      showSnackbar("Please acknowledge the backup restore.", "error");
      return;
    }

    // Only validate password if it's in auth methods
    if (authMethods.includes("password") && !restoreBackupData.password) {
      showSnackbar("Password is required.", "error");
      return;
    }

    // Only validate signature if it's in auth methods
    if (authMethods.includes("signature")) {
      const isSignatureEmpty = restoreSigPadRef.current?.isEmpty() ?? true;
      if (!restoreBackupData.signature || isSignatureEmpty) {
        showSnackbar("Signature is required.", "error");
        return;
      }
    }

    try {
      setIsActionLoading(true);
      const backupId = selectedBackup.id || selectedBackup.backup_id;

      if (!backupId) {
        showSnackbar("Backup ID is required for restore.", "error");
        setIsActionLoading(false);
        return;
      }

      // Prepare audit trail data
      const isPasswordRequired = authMethods.includes("password");
      const passwordValue = isPasswordRequired
        ? restoreBackupData.password || ""
        : "";
      const hash = await generateSHA256(Math.random().toString());
      const auditTrailData = await getAuditTrailData({
        action: "RESTORE_BACKUP",
        comment: restoreBackupData.restoreComments || "-",
        parent_version: selectedBackup.version_id_ref || null,
        is_password_required: isPasswordRequired,
        password: passwordValue || undefined,
        passwordLength: passwordValue?.length || 0,
        version_content: {
          action_to_show: "Restored a Backup",
          backup_name: selectedBackup.backup_name,
          backup_date: formatDate(
            selectedBackup.backup_date || selectedBackup.created_at,
          ),
          backup_type: formatBackupType(
            selectedBackup.backup_type,
            selectedBackup.status,
            selectedBackup.backup_name,
          ),
          restorer: restoreBackupData.restorer,
          restore_comments: restoreBackupData.restoreComments || "",
        },
      });

      // Add hash to audit trail data
      auditTrailData.hash = hash;

      const restoreData = {
        backup_id: backupId,
        restore_comments: restoreBackupData.restoreComments,
        signature: authMethods.includes("signature")
          ? restoreBackupData.signature
          : "",
        ...(authMethods.includes("password") && restoreBackupData.password
          ? { password: restoreBackupData.password }
          : {}),
        ...(!authMethods.includes("password")
          ? {
              is_password_required: false,
            }
          : {
              is_password_required: true,
            }),
        restorer: restoreBackupData.restorer,
        audit_trail_data: auditTrailData,
        time_zone: getAppTimezoneAbbr(appSetupState),
      };

      await dispatch(restoreBackup(restoreData)).unwrap();
      setIsRestoreModalOpen(false);
      setSelectedBackup(null);
      showSnackbar("Backup restored successfully.", "success");
    } catch (error) {
      // Extract error message from various possible locations
      // When using .unwrap(), the error is the value passed to rejectWithValue()
      // which could be a string (message) or an object
      let errorMessage = "Failed to restore backup.";

      // Check if error is a string (from rejectWithValue passing message directly)
      if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object") {
        // Check for {success: false, message: "Incorrect password"} structure
        if (error.success === false && error.message) {
          errorMessage = error.message;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.error) {
          errorMessage = error.error;
        }
      }

      // Fallback to checking nested structures
      if (errorMessage === "Failed to restore backup.") {
        errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          error?.payload?.message ||
          error?.payload?.error ||
          (error?.payload &&
            typeof error.payload === "object" &&
            error.payload.message) ||
          (error?.payload &&
            typeof error.payload === "object" &&
            error.payload.error) ||
          "Failed to restore backup.";
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDelete = async (backup) => {
    try {
      setIsActionLoading(true);
      await dispatch(deleteBackup(backup.id || backup.backup_id));
      const { search, searchType, sortBy, sortOrder } = getSearchParams();
      dispatch(
        fetchBackups({ limit, offset, search, searchType, sortBy, sortOrder }),
      );
      showSnackbar("Backup deleted successfully.", "success");
    } catch (error) {
      showSnackbar("Failed to delete backup.", "error");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDeleteClick = (backup) => {
    const backupName = formatBackupName(backup.backup_name, null, backup);
    setIsDeleteConfirmationOpen(true);
    DeleteConfirmation(
      () => handleDelete(backup),
      `Are you sure you want to delete "${backupName}"? This action cannot be undone.`,
      "Delete Backup",
    ).finally(() => {
      setIsDeleteConfirmationOpen(false);
    });
  };

  // Get latest app setup for auto-backup info
  const latestAppSetup =
    appSetupState?.items?.length > 0
      ? (() => {
          const sortedItems = [...appSetupState.items].sort((a, b) => {
            const dateA = new Date(
              a?.updated_at || a?.created_at || 0,
            ).getTime();
            const dateB = new Date(
              b?.updated_at || b?.created_at || 0,
            ).getTime();
            return dateB - dateA;
          });
          return sortedItems[0];
        })()
      : null;

  // Format auto_backup_time from "03:10:00" to "10:00 PM EST"
  const formatAutoBackupTime = (timeString) => {
    if (!timeString) return "N/A";
    try {
      // Get timezone dynamically to ensure we use the latest app setup
      const currentTzAbbr = getAppTimezoneAbbr(appSetupState);
      const [hours, minutes] = timeString.split(":");
      const hour24 = parseInt(hours, 10);
      const hour12 = hour24 % 12 || 12;
      const ampm = hour24 >= 12 ? "PM" : "AM";
      const mins = minutes || "00";
      return `${hour12}:${mins} ${ampm} ${currentTzAbbr}`;
    } catch (error) {
      return timeString;
    }
  };

  // Get the oldest automatic backup (backup_type === "Auto")
  const getOldestAutomaticBackup = () => {
    if (!backups || backups.length === 0) return null;

    const autoBackups = backups.filter(
      (backup) =>
        backup.backup_type && backup.backup_type.toLowerCase() === "auto",
    );

    if (autoBackups.length === 0) return null;

    // Sort by backup_date ascending and get the oldest one
    const sorted = [...autoBackups].sort((a, b) => {
      const dateA = new Date(a.backup_date || a.created_at || 0).getTime();
      const dateB = new Date(b.backup_date || b.created_at || 0).getTime();
      return dateA - dateB;
    });

    return sorted[0];
  };

  // Get the latest automatic backup (backup_type === "Auto")
  const getLatestAutomaticBackup = () => {
    if (!backups || backups.length === 0) return null;

    const autoBackups = backups.filter(
      (backup) =>
        backup.backup_type && backup.backup_type.toLowerCase() === "auto",
    );

    if (autoBackups.length === 0) return null;

    // Sort by backup_date descending and get the latest one
    const sorted = [...autoBackups].sort((a, b) => {
      const dateA = new Date(a.backup_date || a.created_at || 0).getTime();
      const dateB = new Date(b.backup_date || b.created_at || 0).getTime();
      return dateB - dateA;
    });

    return sorted[0];
  };

  // Format backup date for display (YYYY-MM-DD format)
  const formatBackupDateForDisplay = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (error) {
      return dateString;
    }
  };

  const oldestAutomaticBackup = getOldestAutomaticBackup();
  const latestAutomaticBackup = getLatestAutomaticBackup();

  // Upload to S3 helper function for backups
  const uploadBackupToS3 = async (file) => {
    try {
      // Get authorization token from storage
      const token =
        localStorage.rememberMe === "true"
          ? localStorage.token
          : sessionStorage.token;

      if (!token) {
        throw new Error("No authorization token found");
      }

      // Generate unique filename
      const timestamp = new Date().getTime();
      const fileName = `backup-${timestamp}.zip`;
      const fileType = "application/zip";

      // 1. Get presigned URL from backend
      const response = await fetch(`${BASE_URL}upload/get-upload-urls`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          folder: "backups",
          files: [{ fileName, fileType }],
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to get upload URL: ${response.status}`);
      }

      const responseData = await response.json();
      const { uploadUrl, fileUrl } = responseData.files[0];

      // 2. Upload to S3 using presigned URL (no timeout - backups can be huge)
      const s3Response = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": fileType,
        },
        body: file,
      });

      if (!s3Response.ok) {
        throw new Error(`S3 upload failed: ${s3Response.status}`);
      }

      return fileUrl;
    } catch (error) {
      throw error;
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith(".zip")) {
      showSnackbar("Please select a ZIP file.", "error");
      return;
    }

    try {
      setIsActionLoading(true);
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      showSnackbar(
        `Uploading backup file (${fileSizeMB} MB)... This may take several minutes.`,
        "info",
      );

      // Upload to S3
      const backupUrl = await uploadBackupToS3(file);

      showSnackbar("Upload complete. Restoring backup...", "info");

      // Prepare audit trail data for restore-zip
      const hash = await generateSHA256(Math.random().toString());
      const userName = localStorage.getItem("user_name") || "Unknown User";
      const auditTrailData = await getAuditTrailData({
        action: "RESTORE_BACKUP",
        comment: `Uploaded and restored backup file: ${file.name}`,
        parent_version: null,
        is_password_required: false,
        password: "",
        version_content: {
          action_to_show: "Restored a Backup",
          backup_name: file.name,
          backup_url: backupUrl,
          restorer: userName,
          restore_comments: `Uploaded backup file: ${file.name}`,
        },
      });

      // Add hash to audit trail data
      auditTrailData.hash = hash;

      // Call restore-zip endpoint - ensure loader stays visible until this completes
      const token =
        localStorage.rememberMe === "true"
          ? localStorage.token
          : sessionStorage.token;

      const restoreResponse = await fetch(`${BASE_URL}backup/restore-zip`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          backup_url: backupUrl,
          restorer: userName,
          audit_trail_data: auditTrailData,
          file_name: file.name.replace(/\.[^/.]+$/, ""),
          time_zone: getAppTimezoneAbbr(appSetupState),
        }),
      });

      // Ensure we read the response before checking status
      const restoreData = await restoreResponse.json().catch(() => ({}));

      // Check if response indicates failure (success: false)
      if (restoreData && restoreData.success === false) {
        throw new Error(
          restoreData.message || restoreData.error || "Operation failed",
        );
      }

      if (!restoreResponse.ok) {
        throw new Error(
          restoreData.message ||
            `Failed to restore backup: ${restoreResponse.status}`,
        );
      }

      // Only clear loader after restore-zip API completes successfully
      showSnackbar("Backup restore initiated successfully.", "success");

      // Refresh backups list
      const { search, searchType, sortBy, sortOrder } = getSearchParams();
      dispatch(
        fetchBackups({ limit, offset, search, searchType, sortBy, sortOrder }),
      );
    } catch (error) {
      console.error("Error uploading backup:", error);
      showSnackbar(
        error.message || "Failed to upload backup. Please try again.",
        "error",
      );
    } finally {
      setIsActionLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUploadBackupClick = () => {
    fileInputRef.current?.click();
  };

  const handleExportCSV = async () => {
    await dispatch(exportBackupCSV(getAppTimezoneAbbr(appSetupState)));
  };

  // Manual cleanup function for downloads
  const cleanupDownload = (backupId) => {
    setActiveDownloads((prev) => {
      const newMap = new Map(prev);
      newMap.delete(backupId);
      return newMap;
    });
  };

  // Simple and reliable download monitoring
  const monitorDownload = (backupId, downloadUrl) => {
    // Add to active downloads
    setActiveDownloads((prev) =>
      new Map(prev).set(backupId, {
        url: downloadUrl,
        startTime: Date.now(),
        status: "downloading",
      }),
    );

    // Use a simple timeout approach - this is the most reliable method
    // since browser downloads APIs are not available in regular web pages
    setTimeout(() => {
      if (activeDownloads.has(backupId)) {
        cleanupDownload(backupId);
      }
    }, 10000); // 10 seconds - reasonable time for most downloads
  };

  // Check if any downloads are active
  const hasActiveDownloads = activeDownloads.size > 0;

  // Determine if actions should be disabled
  // Lock conditions (for tab management)
  const shouldDisableActionsDueToLock =
    !isFirstTabByOrder || (isApiLocked && lockedBy);

  // Action conditions (for in-progress operations)
  const shouldDisableActionsDueToInProgress =
    isActionLoading || hasActiveDownloads;

  // Combined condition for button disabling
  const shouldDisableActions =
    shouldDisableActionsDueToLock || shouldDisableActionsDueToInProgress;
  const isActionDisabled = shouldDisableActions;

  return (
    <div
      className={
        isRestoreModalOpen || isDeleteConfirmationOpen ? "" : "backups-page"
      }
    >
      <AppBar
        title="Backups"
        buttons={[]}
        showBlackTitle={true}
        showColorTitle={false}
        showTablesDropdown={false}
        onBack={handleAppBarBack}
      />

      {/* Tab Lock Warning Message */}
      {shouldDisableActionsDueToLock && (
        <div
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            color: "#856404",
            padding: "12px 20px",
            margin: "0 20px 20px 20px",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            position: "relative",
            zIndex: 5000,
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
              fill="#856404"
            />
          </svg>
          <span style={{ fontWeight: "500" }}>
            {!isFirstTabByOrder
              ? "⚠️ This page is opened in another tab. Action buttons are disabled. Only the unlocked tab can perform actions."
              : `This page is currently being edited by ${lockedBy || "another user"}. Action buttons are disabled. You can still search and view data.`}
          </span>
        </div>
      )}

      {/* Auto-Backup Information Section */}
      <div
        style={{ marginBottom: "20px", padding: "0 20px", lineHeight: "1.6" }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            lineHeight: "1.4",
          }}
        >
          <div style={{ marginBottom: "2px" }}>
            <span style={{ color: "#6C757D" }}>Auto-backup is</span>{" "}
            <strong>
              {latestAppSetup?.is_autobackup_enabled ? "enabled" : "disabled"}
            </strong>
            .
          </div>

          {latestAppSetup?.is_autobackup_enabled && (
            <>
              <div style={{ marginBottom: "20px" }}>
                <span style={{ color: "#6C757D" }}>
                  Next scheduled daily backup:
                </span>{" "}
                <strong>
                  {formatAutoBackupTime(latestAppSetup?.auto_backup_time || "")}
                </strong>
                .
              </div>

              <div style={{ marginBottom: "16px" }}>
                <span style={{ color: "#6C757D" }}>
                  Auto-backup Retention policy:
                </span>{" "}
                <span style={{ marginLeft: "8px" }}>
                  <span style={{ color: "#6C757D" }}>
                    Daily backups retained:
                  </span>{" "}
                  <strong>
                    {latestAppSetup?.backup_retention_policy?.daily || "N/A"}
                  </strong>
                  <span style={{ margin: "0 12px" }}>|</span>
                  <span style={{ color: "#6C757D" }}>
                    Weekly backups retained:
                  </span>{" "}
                  <strong>
                    {latestAppSetup?.backup_retention_policy?.weekly || "N/A"}
                  </strong>
                  <span style={{ margin: "0 12px" }}>|</span>
                  <span style={{ color: "#6C757D" }}>
                    Monthly backups retained:
                  </span>{" "}
                  <strong>
                    {latestAppSetup?.backup_retention_policy?.monthly || "N/A"}
                  </strong>
                  <span style={{ margin: "0 12px" }}>|</span>
                  <span style={{ color: "#6C757D" }}>
                    Yearly backups retained:
                  </span>{" "}
                  <strong>
                    {latestAppSetup?.backup_retention_policy?.yearly || "N/A"}
                  </strong>
                </span>
              </div>

              {lastBackupDate && (
                <div>
                  <span style={{ color: "#6C757D" }}>
                    Oldest automatic backup currently retained:
                  </span>{" "}
                  <strong>{formatBackupDateForDisplay(lastBackupDate)}</strong>.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <AppTable
        data={backups}
        loading={loading || localLoading}
        total={totalBackups}
        limit={limit}
        offset={offset}
        columnsData={columns}
        showSearchInput={true}
        searchInputPosition="right"
        extrapadding="0px"
        searchBarLeftContent={
          <div className="create-backup-section">
            <div className="backup-input-wrapper">
              <input
                type="text"
                placeholder="Backup Name"
                value={backupName}
                onChange={(e) => setBackupName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !shouldDisableActions) {
                    handleCreateBackup();
                  }
                }}
                className="backup-name-input"
                disabled={shouldDisableActions}
                style={{
                  opacity: shouldDisableActions ? 0.6 : 1,
                  cursor: shouldDisableActions ? "not-allowed" : "text",
                }}
              />
              <Button
                onClick={handleCreateBackup}
                size="sm"
                className="create-button-joined"
                disabled={shouldDisableActions}
              >
                Create
              </Button>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              accept=".zip"
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
            <Button
              variant="outline-blue"
              size="sm"
              style={{ marginLeft: "10px" }}
              onClick={handleUploadBackupClick}
              disabled={shouldDisableActions}
            >
              Upload Backup
            </Button>
          </div>
        }
        paginate={true}
        pagination={{
          limit: [limit, setLimit],
          offset: [offset, setOffset],
          pagination: {
            limit,
            offset,
            total: totalBackups,
            totalPages: Math.ceil(totalBackups / limit) || 1,
          },
          paginationPosition: {
            position: "right",
            leftContent: (
              <ExportCsvBtn
                handleExportCSV={handleExportCSV}
                buttonLabel="Export CSV"
                whiteVariant={true}
                size="sm"
                disabled={shouldDisableActions}
              />
            ),
          },
        }}
        onFilterSortHandler={handleFilterSort}
      >
        {loading || localLoading ? (
          <tr>
            <td
              colSpan={columns.length}
              style={{ textAlign: "center", padding: "20px" }}
            >
              Loading...
            </td>
          </tr>
        ) : backups && backups.length > 0 ? (
          backups.map((row, index) => (
            <tr key={index}>
              {/* Date */}
              <td>{formatDate(row.backup_date || row.created_at)}</td>

              {/* Name */}
              <td>{formatBackupName(row.backup_name, index, row)}</td>

              {/* Type */}
              <td>
                {formatBackupType(row.backup_type, row.status, row.backup_name)}
              </td>

              {/* Action Buttons */}
              <td>
                <div className="app-action-btns">
                  <button
                    className="download-row"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(row);
                    }}
                    disabled={shouldDisableActions}
                    title={
                      shouldDisableActionsDueToInProgress
                        ? "Action disabled - another action is in progress"
                        : shouldDisableActionsDueToLock
                          ? "Action disabled - page opened in another tab"
                          : "Download"
                    }
                    style={
                      shouldDisableActions
                        ? { opacity: 0.5, cursor: "not-allowed" }
                        : {}
                    }
                  >
                    <svg
                      viewBox="0 0 35 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="35" height="24" rx="5" fill="#0D6EFD" />
                      <path
                        d="M17.5 15.5L13.125 11.125L14.35 9.85625L16.625 12.1313V5H18.375V12.1313L20.65 9.85625L21.875 11.125L17.5 15.5ZM12.25 19C11.7687 19 11.3568 18.8286 11.0141 18.4859C10.6714 18.1432 10.5 17.7312 10.5 17.25V14.625H12.25V17.25H22.75V14.625H24.5V17.25C24.5 17.7312 24.3286 18.1432 23.9859 18.4859C23.6432 18.8286 23.2312 19 22.75 19H12.25Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                  <button
                    className="restore-row"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRestoreClick(row);
                    }}
                    disabled={shouldDisableActions}
                    title={
                      shouldDisableActionsDueToInProgress
                        ? "Action disabled - another action is in progress"
                        : shouldDisableActionsDueToLock
                          ? "Action disabled - page opened in another tab"
                          : "Restore this backup to the current system state"
                    }
                    style={
                      shouldDisableActions
                        ? { opacity: 0.5, cursor: "not-allowed" }
                        : {}
                    }
                  >
                    <svg
                      viewBox="0 0 35 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="1"
                        y="1"
                        width="33"
                        height="22"
                        rx="4"
                        fill="white"
                      />
                      <rect
                        x="1"
                        y="1"
                        width="33"
                        height="22"
                        rx="4"
                        stroke="#0D6EFD"
                        strokeWidth="2"
                      />
                      <path
                        d="M17.5 13.5556C17.0722 13.5556 16.706 13.4032 16.4014 13.0986C16.0968 12.794 15.9444 12.4278 15.9444 12C15.9444 11.5722 16.0968 11.206 16.4014 10.9014C16.706 10.5968 17.0722 10.4444 17.5 10.4444C17.9278 10.4444 18.294 10.5968 18.5986 10.9014C18.9032 11.206 19.0556 11.5722 19.0556 12C19.0556 12.4278 18.9032 12.794 18.5986 13.0986C18.294 13.4032 17.9278 13.5556 17.5 13.5556ZM17.5 19C15.6981 19 14.1361 18.4069 12.8139 17.2208C11.4917 16.0347 10.7333 14.5537 10.5389 12.7778H12.1333C12.3148 14.1259 12.9144 15.2407 13.9319 16.1222C14.9495 17.0037 16.1389 17.4444 17.5 17.4444C19.0167 17.4444 20.3032 16.9162 21.3597 15.8597C22.4162 14.8032 22.9444 13.5167 22.9444 12C22.9444 10.4833 22.4162 9.19676 21.3597 8.14028C20.3032 7.0838 19.0167 6.55556 17.5 6.55556C16.6056 6.55556 15.7694 6.76296 14.9917 7.17778C14.2139 7.59259 13.5593 8.16296 13.0278 8.88889H15.1667V10.4444H10.5V5.77778H12.0556V7.60556C12.7167 6.77593 13.5236 6.13426 14.4764 5.68056C15.4292 5.22685 16.437 5 17.5 5C18.4722 5 19.3829 5.18472 20.2319 5.55417C21.081 5.92361 21.8199 6.42269 22.4486 7.05139C23.0773 7.68009 23.5764 8.41898 23.9458 9.26806C24.3153 10.1171 24.5 11.0278 24.5 12C24.5 12.9722 24.3153 13.8829 23.9458 14.7319C23.5764 15.581 23.0773 16.3199 22.4486 16.9486C21.8199 17.5773 21.081 18.0764 20.2319 18.4458C19.3829 18.8153 18.4722 19 17.5 19Z"
                        fill="#0D6EFD"
                      />
                    </svg>
                  </button>
                  <DeleteRowButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(row);
                    }}
                    disabled={shouldDisableActions || isDeleteDisabled(row)}
                    title={
                      shouldDisableActionsDueToInProgress
                        ? "Action disabled - another action is in progress"
                        : shouldDisableActionsDueToLock
                          ? "Action disabled - page opened in another tab"
                          : isDeleteDisabled(row)
                            ? "Action disabled - backup is currently being downloaded"
                            : "Delete"
                    }
                  />
                </div>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td
              colSpan={columns.length}
              style={{ textAlign: "center", padding: "20px" }}
            >
              No records found
            </td>
          </tr>
        )}
      </AppTable>

      {/* Restore Backup Modal */}
      {isRestoreModalOpen && selectedBackup && (
        <div className="disable-modal-overlay">
          <div className="disable-modal-content">
            <div className="disable-modal-header-container">
              <h3 className="disable-modal-title">
                <span>Warning: You are Attempting to Restore a Backup</span>
              </h3>
              <button
                onClick={() => {
                  setIsRestoreModalOpen(false);
                  setSelectedBackup(null);
                  setRestoreBackupData({
                    restoreComments: "",
                    acknowledgeBackup: false,
                    password: "",
                    signature: "",
                    restorer: localStorage.getItem("user_name") || "",
                  });
                }}
                className="disable-modal-close-button"
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} size="2x" color="gray" />
              </button>
            </div>

            <hr className="divider" />

            <div className="disable-modal-form-group">
              <label
                className="disable-modal-label"
                style={{ marginBottom: "4px" }}
              >
                The Following Backup will be Restored:
              </label>
              <div>
                <span className="disable-modal-procedures-text">
                  {formatBackupName(
                    selectedBackup.backup_name,
                    null,
                    selectedBackup,
                  )}{" "}
                  taken on{" "}
                  {formatDateForRestore(
                    selectedBackup.backup_date || selectedBackup.created_at,
                  )}{" "}
                  by{" "}
                  {formatBackupTypeForRestore(
                    selectedBackup.backup_type,
                    selectedBackup.status,
                    selectedBackup.backup_name,
                  )
                    .replace(/^By\s+/i, "")
                    .trim()}
                </span>
              </div>
            </div>

            <div className="disable-modal-form-group">
              <div className="disable-modal-disabled-by" style={{ gap: "5px" }}>
                <label className="disable-modal-disabled-by-label">
                  Restorer
                </label>
                <span
                  className="disable-modal-disabled-by-text"
                  style={{
                    textDecoration: "underline",
                    color: "#212529",
                    fontWeight: "normal",
                  }}
                >
                  {restoreBackupData.restorer}
                </span>
              </div>
            </div>

            <div className="disable-modal-form-group">
              <TextArea
                label={
                  <>
                    Restore Backup Comments <span className="required">*</span>
                  </>
                }
                placeholder="Enter restore backup comments..."
                value={restoreBackupData.restoreComments}
                onChange={(e) =>
                  setRestoreBackupData((prev) => ({
                    ...prev,
                    restoreComments: e.target.value,
                  }))
                }
                labelStyle={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  marginBottom: "8px",
                }}
              />
            </div>

            <div className="disable-modal-form-group">
              <CheckboxField
                label={
                  <span className="restore-backup-checkbox-label-text">
                    I acknowledge a Backup of the System will be saved and not
                    deleteable from Backups List. The Backup File will be
                    downloaded, and I will download the file to a secure
                    location. The Audit Trail will match the audit trail of the
                    backup, with an entry documenting the restore action, the
                    file used for restore with checksum, and the name of the
                    file downloaded and checksum.{" "}
                    <span className="required">*</span>
                  </span>
                }
                checked={restoreBackupData.acknowledgeBackup}
                onChange={(e) =>
                  setRestoreBackupData((prev) => ({
                    ...prev,
                    acknowledgeBackup: e.target.checked,
                  }))
                }
                labelStyle={{
                  fontSize: "14px",
                  fontWeight: "normal",
                }}
                style={{
                  fontWeight: "normal",
                }}
                className="restore-backup-checkbox-field"
              />
            </div>

            {/* Password - Only show if password is in auth methods */}
            {authMethods.includes("password") && (
              <div className="disable-modal-form-group">
                <TextField
                  label={
                    <>
                      Enter Password <span className="required">*</span>
                    </>
                  }
                  type="password"
                  placeholder="Enter your password"
                  value={restoreBackupData.password}
                  onChange={(e) =>
                    setRestoreBackupData((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  labelStyle={{ fontSize: "14px", fontWeight: "bold" }}
                />
              </div>
            )}

            {/* Signature - Only show if signature is in auth methods */}
            {authMethods.includes("signature") && (
              <div className="disable-modal-form-group">
                <SignatureField
                  ref={restoreSigPadRef}
                  label={
                    <>
                      Sign Here <span className="required">*</span>
                    </>
                  }
                  width="100%"
                  height="100px"
                  onEnd={handleRestoreSignatureEnd}
                  labelStyle={{ fontSize: "14px", fontWeight: "bold" }}
                />
              </div>
            )}

            <div className="disable-modal-footer">
              <button
                onClick={() => {
                  setIsRestoreModalOpen(false);
                  setSelectedBackup(null);
                  setRestoreBackupData({
                    restoreComments: "",
                    acknowledgeBackup: false,
                    password: "",
                    signature: "",
                    restorer: localStorage.getItem("user_name") || "",
                  });
                }}
                className="disable-modal-close-btn"
              >
                Close
              </button>
              <button
                onClick={handleRestoreBackup}
                className="disable-modal-disable-btn"
                disabled={shouldDisableActions}
                style={
                  shouldDisableActions
                    ? { opacity: 0.5, cursor: "not-allowed" }
                    : {}
                }
                title={
                  shouldDisableActionsDueToInProgress
                    ? "Action disabled - another action is in progress"
                    : shouldDisableActionsDueToLock
                      ? "Action disabled - page opened in another tab"
                      : "Restore Backup"
                }
              >
                Restore Backup
              </button>
            </div>
          </div>
        </div>
      )}

      {isActionLoading && <Loader text="Processing..." />}
      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
    </div>
  );
};

export default Backups;
